import { cn } from '@/utils'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import {
  TEXT_ON_PATH_COLOR_SWATCHES,
  TEXT_ON_PATH_COLOR_SWATCHES_PER_ROW,
} from '@/lib/shapes/text-on-path-colors'

import { TRIGGER_CLASS } from '../constants/format-control-classes'
import { useHexColorInput } from '../hooks/use-hex-color-input'
import type { ControlProps } from '../types/control-props'
import { FormatPopoverContent } from './format-popover-content'

export function TextOnPathColorControl({
  config,
  onConfigChange,
  onRestoreFocus,
  className,
}: ControlProps) {
  const { normalized, hexInput, apply, handleHexFocus, handleHexChange, handleHexBlur } =
    useHexColorInput(config.colorHex, onConfigChange)

  return (
    <Popover>
      {/*
        Ungrouped in the design (node 20416:459700, item 6): a 32px ghost square around the 16px
        swatch, sitting between the two segmented group boxes. `TRIGGER_CLASS`'s 12px horizontal
        padding would otherwise make it 40px wide.
      */}
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Text color"
          className={cn(TRIGGER_CLASS, 'h-8 w-8 justify-center px-0', className)}
        >
          <span
            className="block h-4 w-4 rounded-[3px] border border-black/20"
            style={{ backgroundColor: config.colorHex }}
          />
        </button>
      </PopoverTrigger>
      <FormatPopoverContent className="w-[280px]" onRestoreFocus={onRestoreFocus}>
        <div className="flex flex-col gap-3">
          <span className="text-xs font-medium text-muted-foreground">Color</span>
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: `repeat(${TEXT_ON_PATH_COLOR_SWATCHES_PER_ROW}, minmax(0, 1fr))`,
            }}
          >
            {TEXT_ON_PATH_COLOR_SWATCHES.map((swatch) => (
              <button
                key={swatch}
                type="button"
                aria-label={swatch}
                aria-pressed={normalized === swatch}
                onClick={() => apply(swatch)}
                className={cn(
                  'h-6 w-full rounded-[4px] border outline-none transition-shadow focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  normalized === swatch
                    ? 'border-transparent ring-2 ring-sky-600 ring-offset-1'
                    : 'border-black/15 hover:border-black/40'
                )}
                style={{ backgroundColor: swatch }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-black/[0.08] pt-3">
            <input
              type="color"
              aria-label="Custom color"
              // The native picker only accepts #rrggbb, so a config holding any other CSS colour
              // form falls back to black here rather than silently rejecting the attribute.
              value={normalized ?? '#000000'}
              onChange={(e) => apply(e.target.value.toUpperCase())}
              className="h-8 w-10 shrink-0 cursor-pointer rounded border border-black/15 bg-transparent p-0.5"
            />
            <Input
              size="compact"
              aria-label="Hex color"
              value={hexInput}
              spellCheck={false}
              onFocus={handleHexFocus}
              onChange={handleHexChange}
              onBlur={handleHexBlur}
            />
          </div>
        </div>
      </FormatPopoverContent>
    </Popover>
  )
}
